import api, { convertToISODateTime, handleApiError } from './client';

export const rollCallService = {
  // Get roll calls for a specific date and group
  async getRollCalls(groupId, date) {
    try {
      const response = await api.get('/api/roll-calls', {
        params: {
          groupId,
          date: convertToISODateTime(date),
        },
      });

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Get roll calls error:', error);
      return handleApiError(error, "Errore nel caricamento dell'appello");
    }
  },

  // Get daily roll calls summary across all groups for a specific date
  async getDailyRollCalls(date) {
    try {
      const response = await api.get('/api/roll-calls/daily', {
        params: {
          date: convertToISODateTime(date),
        },
      });

      return {
        success: true,
        data: Array.isArray(response.data) ? response.data : [],
      };
    } catch (error) {
      console.error('Get daily roll calls error:', error);
      return handleApiError(error, "Errore nel caricamento degli appelli del giorno");
    }
  },


  // Create roll calls for all active users in a group
  async createRollCalls(groupId, date) {
    try {
      const response = await api.post('/api/roll-calls', {
        groupId,
        date: convertToISODateTime(date),
      });

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Create roll calls error:', error);
      return handleApiError(error, "Errore nella creazione dell'appello");
    }
  },

  // Update attendance for a roll call
  async updateRollCall(rollCallId, isPresent, notes = null) {
    try {
      const response = await api.put(`/api/roll-calls/${rollCallId}`, {
        isPresent,
        notes,
      });

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Update roll call error:', error);
      return handleApiError(error, "Errore nell'aggiornamento della presenza");
    }
  },

  // Check if roll calls exist for a specific date and group
  async checkRollCalls(groupId, date) {
    try {
      const result = await this.getRollCalls(groupId, date);
      if (result.success) {
        return {
          success: true,
          exists: result.data.length > 0,
          data: result.data,
        };
      }
      return { success: false, exists: false, data: [], message: result.message };
    } catch (error) {
      console.error('Error checking roll calls:', error);
      return { success: false, exists: false, data: [], message: "Errore nel controllo dell'appello" };
    }
  },

  // Delete roll call session for a group and date (Admin only)
  async deleteRollCallSession(groupId, date) {
    try {
      const response = await api.delete('/api/roll-calls', {
        params: {
          groupId,
          date: convertToISODateTime(date),
        },
      });

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Delete roll call session error:', error);
      return handleApiError(error, "Errore durante l'eliminazione dell'appello");
    }
  },

  // Get dates with roll calls for a specific month
  async getRollCallDates(groupId, year, month) {
    try {
      const response = await api.get('/api/roll-calls/dates', {
        params: { groupId, year, month },
      });

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Get roll call dates error:', error);
      return handleApiError(error, 'Errore nel caricamento delle date degli appelli');
    }
  },

  // Get user entries status (for pay-per-use subscriptions)
  async getUserEntriesStatus(userId, date = null, subscriptionIds = null) {
    try {
      const params = {
        userId,
        date: date ? convertToISODateTime(date) : undefined,
      };

      if (subscriptionIds?.length) {
        params.subscriptionIds = subscriptionIds;
      }

      const response = await api.get('/api/roll-calls/entries-status', { params });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Get user entries status error:', error);
      return handleApiError(error, 'Errore nel caricamento dello status ingressi');
    }
  },

  // Count user attendance in a specific period (for pay-per-use subscriptions)
  async countUserAttendanceInPeriod(userId, groupId, startDate, endDate) {
    try {
      const response = await api.get('/api/roll-calls/attendance-count', {
        params: {
          userId,
          groupId,
          startDate: convertToISODateTime(startDate),
          endDate: convertToISODateTime(endDate),
        },
      });

      const count = response.data?.count ?? response.data;
      return {
        success: true,
        data: count,
      };
    } catch (error) {
      console.error('Count user attendance in period error:', error);
      return handleApiError(error, 'Errore nel conteggio presenze');
    }
  },

  // Check discrepancies between enrolled users and roll call participants
  async checkRollCallDiscrepancies(groupId, date) {
    try {
      const response = await api.get('/api/roll-calls/discrepancies', {
        params: {
          groupId,
          date: convertToISODateTime(date),
        },
      });

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Check roll call discrepancies error:', error);
      return handleApiError(error, 'Errore nel controllo delle discrepanze');
    }
  },

  // Refresh roll call: sync participants with valid subscriptions
  async refreshRollCall(groupId, date) {
    try {
      const response = await api.post('/api/roll-calls/refresh', {
        groupId,
        date: convertToISODateTime(date),
      });

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Refresh roll call error:', error);
      return handleApiError(error, "Errore nell'aggiornamento dell'appello");
    }
  },

  // Get courses list for attendance statistics in date range
  async getStatisticsCourses(startDate, endDate) {
    try {
      const response = await api.get('/api/roll-calls/statistics/courses', {
        params: {
          startDate: convertToISODateTime(startDate),
          endDate: convertToISODateTime(endDate),
        },
      });

      return {
        success: true,
        data: Array.isArray(response.data) ? response.data : [],
      };
    } catch (error) {
      console.error('Get statistics courses error:', error);
      return handleApiError(error, 'Errore nel caricamento dei corsi per le statistiche');
    }
  },

  // Get attendance statistics for all courses (or a specific course if courseId provided)
  async getCourseAttendanceStatistics(courseId, startDate, endDate, academicYear = null) {
    try {
      const params = {
        startDate: convertToISODateTime(startDate),
        endDate: convertToISODateTime(endDate),
      };
      if (courseId) {
        params.courseId = courseId;
      }
      if (academicYear) {
        params.academicYear = academicYear;
      }

      const response = await api.get('/api/roll-calls/statistics', {
        params,
      });

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Get course attendance statistics error:', error);
      return handleApiError(error, 'Errore nel caricamento delle statistiche di presenza');
    }
  },

  // Alias to get all courses attendance statistics
  async getAllCoursesAttendanceStatistics(startDate, endDate, academicYear = null) {
    return this.getCourseAttendanceStatistics(null, startDate, endDate, academicYear);
  },
};

export default rollCallService;
